import { defineRule } from "vee-validate";
import {
  required as requiredRule,
  email as emailRule,
  confirmed as confirmedRule,
  max as maxLenRule,
  min as minLenRule,
  max_value as maxValueRule,
  min_value as minValueRule,
  alpha_dash as alphaDashRule,
  between as betweenRule,
  integer as integerRule,
} from "@vee-validate/rules";
import { DateTime } from "luxon";

function fieldName(ctx: any): string {
  return ctx?.field || "This field";
}

/**
 * Basic built-ins with custom messages
 */

defineRule("required", (value: unknown, _params, ctx) => {
  if (!requiredRule(value)) {
    return `${fieldName(ctx)} is required`;
  }
  return true;
});

defineRule("email", (value: unknown, _params, ctx) => {
  // Let "required" handle empties
  if (value === null || value === undefined || String(value).length === 0) {
    return true;
  }

  if (!emailRule(value)) {
    return `${fieldName(ctx)} must be a valid email`;
  }
  return true;
});

defineRule("confirmed", (value: unknown, [target]: any[] = [], _ctx) => {
  if (!confirmedRule(value, { target })) {
    return `Passwords must match`;
  }
  return true;
});

defineRule("max", (value: unknown, [length]: any[] = [], ctx) => {
  if (!maxLenRule(value, { length })) {
    return `${fieldName(ctx)} may not be greater than ${length} characters`;
  }
  return true;
});

defineRule("min", (value: unknown, [length]: any[] = [], ctx) => {
  if (!minLenRule(value, { length })) {
    return `${fieldName(ctx)} must be greater than ${length} characters`;
  }
  return true;
});

defineRule("max_value", (value: unknown, [max]: any[] = [], ctx) => {
  if (!maxValueRule(value, { max })) {
    return `${fieldName(ctx)} must be less than ${max}`;
  }
  return true;
});

defineRule("min_value", (value: unknown, [min]: any[] = [], ctx) => {
  if (!minValueRule(value, { min })) {
    return `${fieldName(ctx)} must be greater than ${min}.`;
  }
  return true;
});

defineRule("alpha_dash", (value: unknown, _params, ctx) => {
  if (value === null || value === undefined || String(value).length === 0) {
    return true;
  }
  if (!alphaDashRule(value, {})) {
    return `${fieldName(ctx)} must only contain alphabetic characters, numbers, dashes, and underscores`;
  }
  return true;
});

/**
 * URL
 */

defineRule("url", (value?: string | null, _params?: unknown, ctx?: any) => {
  if (!value) {
    return true; // let "required" decide emptiness
  }

  const ok =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(
      value,
    );

  return ok || `${fieldName(ctx)} must be a valid URL`;
});

/**
 * SQL date (Luxon)
 */

defineRule("sql_date", (value: string, _params, ctx) => {
  const dt = DateTime.fromSQL(value);
  const ok = dt.isValid;
  return (
    ok || `${fieldName(ctx)} must be a valid SQL date (YYYY-MM-DD HH:MM:SS)`
  );
});

// sql_date_after
defineRule("sql_date_after", (value: string, params: string[] = [], ctx) => {
  if (!value) return true;

  const current = DateTime.fromSQL(value);
  if (!current.isValid) {
    return `${fieldName(ctx)} must be a valid SQL date (YYYY-MM-DD HH:MM:SS)`;
  }

  const dateTimes = params
    .filter((v) => v != null)
    .map((v) => DateTime.fromSQL(v))
    .filter((dt) => dt.isValid);

  if (!dateTimes.length) return true;

  // latest in params
  let latest = dateTimes[0];
  for (const dt of dateTimes) {
    if (dt > latest) latest = dt;
  }

  if (current > latest) {
    return true;
  }

  return `${fieldName(ctx)} must be after ${latest.toSQL({ includeOffset: false })}`;
});

// sql_date_before
defineRule("sql_date_before", (value: string, params: string[] = [], ctx) => {
  if (!value) return true;

  const current = DateTime.fromSQL(value);
  if (!current.isValid) {
    return `${fieldName(ctx)} must be a valid SQL date (YYYY-MM-DD HH:MM:SS)`;
  }

  const dateTimes = params
    .filter((v) => v != null)
    .map((v) => DateTime.fromSQL(v))
    .filter((dt) => dt.isValid);

  if (!dateTimes.length) return true;

  // earliest in params
  let earliest = dateTimes[0];
  for (const dt of dateTimes) {
    if (dt < earliest) earliest = dt;
  }

  if (current < earliest) {
    return true;
  }

  return `${fieldName(ctx)} must be before ${earliest.toSQL({ includeOffset: false })}`;
});

// not_equal
defineRule("not_equal", (value: any, [otherValue]: any[] = [], ctx) => {
  return (
    value !== otherValue || `${fieldName(ctx)} must not equal the other value`
  );
});

// double – simple numeric check
defineRule("double", (value: any, _params, ctx) => {
  if (value === null || value === undefined || value === "") return true;
  const num = Number(value);
  const ok = typeof num === "number" && !Number.isNaN(num);
  return ok || `${fieldName(ctx)} must be a valid number`;
});

// less_than
defineRule("less_than", (value: number, params: any[] = [], ctx) => {
  if (value === null || value === undefined || value === ("" as any)) {
    return true;
  }

  const nums = params
    .filter((v) => v != null)
    .map((v) => Number(v))
    .filter((n) => !Number.isNaN(n));

  if (!nums.length) return true;

  const maximum = nums.sort((a, b) => a - b)[0];
  return value < maximum || `${fieldName(ctx)} must be less than ${maximum}`;
});

// greater_than
defineRule("greater_than", (value: number, params: any[] = [], ctx) => {
  if (value === null || value === undefined || value === ("" as any)) {
    return true;
  }

  const nums = params
    .filter((v) => v != null)
    .map((v) => Number(v))
    .filter((n) => !Number.isNaN(n));

  if (!nums.length) return true;

  const minimum = nums.sort((a, b) => b - a)[0];
  return value > minimum || `${fieldName(ctx)} must be greater than ${minimum}`;
});

defineRule("no_trailing_slash", (value: string, _params, ctx) => {
  if (!value) return true;
  return !value.endsWith("/") || `${fieldName(ctx)} cannot end with a slash`;
});

defineRule("google_cloud_storage_path", (value: string, _params, ctx) => {
  if (!value) {
    return `${fieldName(ctx)} must be a valid Google Cloud Storage path`;
  }

  const ok =
    value.startsWith("gs://") && !value.includes("\n") && !value.includes("\r");

  return ok || `${fieldName(ctx)} must be a valid Google Cloud Storage path`;
});

defineRule("valid_edge_job_destination", (value: string, _params, ctx) => {
  if (!value) {
    return `${fieldName(ctx)} must be either a Google Storage URI or a t15-assuresrv URI`;
  }

  const ok =
    (value.startsWith("gs://") || value.startsWith("t15-assuresrv://")) &&
    !value.includes("\n") &&
    !value.includes("\r");

  return (
    ok ||
    `${fieldName(ctx)} must be either a Google Storage URI, or t15-assuresrv URI`
  );
});

defineRule("no_hash_character", (value: string, _params, ctx) => {
  if (!value) return true;
  return (
    !value.includes("#") ||
    `${fieldName(ctx)} must not contain a hash character (#)`
  );
});

defineRule("no_gcp_wildcards", (value: string, _params, ctx) => {
  if (!value) return true;

  const ok =
    !value.includes("*") &&
    !value.includes("[") &&
    !value.includes("]") &&
    !value.includes("?");

  return (
    ok || `${fieldName(ctx)} must not contain a wildcard character (*, [, ], ?)`
  );
});

/**
 * Password strength
 */

defineRule("verify_password", (value: string) => {
  if (!value) return true;

  const strongRegex = new RegExp(
    "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[`~!@#$%^&*()_=+,<.>/?|{}-])(?=.{8,})",
  );

  return (
    strongRegex.test(value) ||
    "Passwords must contain at least one of each: uppercase letter, lowercase letter, number, special character"
  );
});

/**
 * Serial number
 */

defineRule("serial_number", (value: string) => {
  if (!value) return true;
  return (
    /^[a-zA-Z0-9\-.]+$/.test(value) ||
    "Serial numbers may only consist of numbers, letters, dashes, and periods"
  );
});

/**
 * IP address
 */

defineRule("ip", (value: string) => {
  if (!value) return true;

  const ok =
    value.match(
      // eslint-disable-next-line no-useless-escape
      /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/,
    ) !== null;

  return ok || "Invalid IP Address";
});

defineRule("between", (value: unknown, [min, max]: any[] = [], ctx) => {
  if (!betweenRule(value, { min, max })) {
    return `${fieldName(ctx)} must be between ${min} and ${max}`;
  }
  return true;
});

defineRule("integer", (value: unknown, _params, ctx) => {
  if (!integerRule(value)) {
    return `${fieldName(ctx)} must be an integer`;
  }
  return true;
});
